// What this device can actually be asked to render.
//
// The scene was authored for a desktop GPU: a 12 MB masterplan, streaming
// photoreal tiles, a seven-effect post chain and 120 skinned figures. A phone
// runs the same page with a fraction of the memory and a hard per-tab ceiling
// the browser enforces by killing the tab, which is what "it crashes on mobile"
// was. Everything expensive reads its budget from here rather than each call
// site inventing its own check.

interface DeviceProfile {
    mobile: boolean;
    /** Ceiling for renderer.setPixelRatio. */
    pixelRatio: number;
    /**
     * SSAO (with its normal pass), depth of field and god rays.
     *
     * Three half-float render targets and the scene drawn a second time for
     * normals. On a phone that is most of the frame budget and a large part of
     * the memory, for effects that are close to invisible on a 6" screen.
     */
    heavyPostFx: boolean;
    shadowMapSize: number;
    pedestrians: number;
    /**
     * Whether to pull all three apartment models down in the background.
     *
     * A convenience on desktop, ~8 MB of GLB plus their decoded geometry held
     * for a popup that may never open on a phone. They still load on demand.
     */
    preloadApartments: boolean;
    /** Tile screen-space error before the model lands, and after. */
    tilesPreloadError: number;
    tilesInteractiveError: number;
    /** Ceiling for the tile cache, in bytes. The library defaults to 0.4 GB. */
    tileCacheBytes: number;
}

function detect(): DeviceProfile {
    // Coarse pointer plus a small screen, rather than a user-agent string: it
    // catches phones and tablets including iPads, which report a desktop UA.
    // deviceMemory is Chromium-only, so it can lower the verdict but never
    // raise it.
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    const small = window.matchMedia('(max-width: 1024px)').matches;
    const lowMemory = (navigator as { deviceMemory?: number }).deviceMemory !== undefined
        ? (navigator as { deviceMemory?: number }).deviceMemory! <= 4
        : false;
    const mobile = (coarse && small) || lowMemory;

    if (!mobile) {
        return {
            mobile: false,
            pixelRatio: 1.5,
            heavyPostFx: true,
            shadowMapSize: 2048,
            pedestrians: 120,
            preloadApartments: true,
            tilesPreloadError: 40,
            tilesInteractiveError: 18,
            tileCacheBytes: 0.4 * 1024 * 1024 * 1024,
        };
    }

    return {
        mobile: true,
        // 1.0 on a 3x phone screen is a ninth of the pixels the desktop cap
        // asks for. Nothing else here comes close to that saving.
        pixelRatio: 1,
        heavyPostFx: false,
        shadowMapSize: 1024,
        pedestrians: 40,
        preloadApartments: false,
        // Coarser tiles: fewer, smaller meshes for a screen that cannot show
        // the difference anyway.
        tilesPreloadError: 64,
        tilesInteractiveError: 32,
        tileCacheBytes: 0.12 * 1024 * 1024 * 1024,
    };
}

export const device: DeviceProfile = detect();
